import React, { useEffect, useLayoutEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { NavbarActionsProvider } from '../../../context/NavbarActionsContext';
import TopLoadingBar from '../../TopLoadingBar';
import MfaGuardGate from '../../MfaGuardGate';
import { SidebarProvider } from '../../ui/sidebar';
import { useResolvedNav } from '../../../hooks/useResolvedNav';
import AppSidebar from '../shell/AppSidebar';
import CommandPalette from '../shell/CommandPalette';
import MobileAppBar from './MobileAppBar';
import MobileCardHead from './MobileCardHead';
import '../../../styles/appShellMobile.css';

// Routes that own their full height (their own toolbars and scroll regions).
const FULL_BLEED_PREFIXES = ['/forecast-v3', '/dashboard/night-audit', '/chat'];
const isFullBleed = (pathname) =>
  FULL_BLEED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Phone shell for the New look (Settings → Appearance → Layout → New).
 * The desktop sidebar folds into a palette-coloured app bar; the page sits on
 * the same inset card as desktop, with a sticky card head for the page
 * switcher and actions. The document scrolls; fixed masks keep the card's
 * gaps clean. Classic phones keep Layout/MobileLayout.
 */
export default function MobileInsetLayout() {
  const { nav, trail, pages, pathname } = useResolvedNav();

  useEffect(() => {
    const els = [document.documentElement, document.body];
    els.forEach((el) => el.classList.add('app-shell-inset', 'app-shell-mobile'));
    return () => els.forEach((el) => el.classList.remove('app-shell-inset', 'app-shell-mobile'));
  }, []);

  // New page, new scroll: the document scrolls, so a page opened from a
  // scrolled dashboard would otherwise start mid-way. A shared link's ?sy=
  // (useScrollUrlSync) still wins.
  useLayoutEffect(() => {
    if (!new URLSearchParams(window.location.search).has('sy')) window.scrollTo(0, 0);
  }, [pathname]);

  const fullBleed = isFullBleed(pathname);

  return (
    <NavbarActionsProvider>
      <SidebarProvider className="app-m-shell flex-col">
        <MfaGuardGate />
        <TopLoadingBar />

        <MobileAppBar />
        <AppSidebar nav={nav} pathname={pathname} />

        <div className="app-shell-card app-m-card">
          <MobileCardHead trail={trail} />
          <main className={fullBleed ? 'app-m-bleed' : 'app-m-body mobile-main-content'}>
            <Outlet />
          </main>
        </div>

        <div aria-hidden className="app-m-mask app-m-mask-top" />
        <div aria-hidden className="app-m-mask app-m-mask-bottom" />

        <CommandPalette nav={nav} pages={pages} />
      </SidebarProvider>
    </NavbarActionsProvider>
  );
}
